/**
 * THE TYPED CLIENT. Six operations, each named exactly as the spec names it.
 *
 *   getAuthorSession      GET  /v1/me                  200 · 401 · 503          (no 403)
 *   getPlatformContacts   GET  /v1/platform-contacts   200 · 401 · 403          (NO 503 — see below)
 *   getCurrentTerms       GET  /v1/terms/current       200 · 401 · 503
 *   acceptTerms           POST /v1/terms/acceptances   201 · 400 · 401 · 404 · 409 · 429
 *   listAgreements        GET  /v1/agreements          200 · 401 · 503        (design 25, 3.1)
 *   acceptAgreements      POST /v1/agreements/acceptances  201 · 400 · 401 · 404 · 503
 *
 * The declared status sets above are the spec's, and the screens above this file branch on them.
 * Two things the corpus does NOT declare are handled anyway, and both are recorded as findings
 * rather than quietly absorbed:
 *
 *   - `getPlatformContacts` declares no 503. Nothing about that route makes it immune — it reads
 *     the same Postgres through the same process — so a 5xx from it is treated as retryable.
 *   - `acceptTerms` declares no 503 while `getCurrentTerms` does. Same treatment, same reason.
 *
 * EVERY RESPONSE IS NARROWED AT RUNTIME. The transport hands back `unknown`, and a client that
 * casts is a client that renders `undefined` into a support page when a proxy returns an error
 * document with a 200. The guards below are the smallest thing that catches that.
 */
import type {
  AgreementAcceptRequest,
  AgreementList,
  AuthorSession,
  PlatformContacts,
  TermsAcceptanceRequest,
  TermsState,
} from './contract';
import { API_PATHS } from './contract';
import { ApiError, ThrottledError } from './errors';
import type { ApiRawResponse, ApiTransport } from './transport';
import { readProblem } from './transport';

/**
 * Turn any non-2xx into an `ApiError`, keeping the status and the problem body - and for a 429 the
 * wait its `Retry-After` named, which is what lets the interstitial say how long.
 */
function refuse(response: ApiRawResponse, operationId: string): ApiError {
  const problem = readProblem(response.body);
  const message = `${operationId} answered ${String(response.status)}`;
  if (response.status === 429) {
    return new ThrottledError(problem, message, response.retryAfterSeconds);
  }
  return new ApiError('http', response.status, problem, message);
}

function shapeError(response: ApiRawResponse, operationId: string, expected: string): ApiError {
  return new ApiError(
    'shape',
    response.status,
    null,
    `${operationId} answered ${String(response.status)} with a body that is not ${expected}.`,
  );
}

function asRecord(body: unknown): Record<string, unknown> | null {
  return typeof body === 'object' && body !== null && !Array.isArray(body)
    ? (body as Record<string, unknown>)
    : null;
}

function isNullableString(value: unknown): boolean {
  return value === null || typeof value === 'string';
}

/**
 * `PlatformContacts`: both keys REQUIRED, both values `string | null`. The key check is the point —
 * a body that omits `editorialEmail` is not "editorial absent", it is a body that does not match
 * the contract, and the two must not be confused on this page of all pages.
 */
function narrowPlatformContacts(body: unknown): PlatformContacts | null {
  const record = asRecord(body);
  if (record === null) {
    return null;
  }
  if (!('supportEmail' in record) || !('editorialEmail' in record)) {
    return null;
  }
  if (!isNullableString(record.supportEmail) || !isNullableString(record.editorialEmail)) {
    return null;
  }
  return body as PlatformContacts;
}

/** `TermsState`: five required members. `acceptedAt` and `blocksSubmission` are optional. */
function narrowTermsState(body: unknown): TermsState | null {
  const record = asRecord(body);
  if (record === null) {
    return null;
  }
  if (
    typeof record.versionLabel !== 'string' ||
    typeof record.effectiveFrom !== 'string' ||
    typeof record.contentUrl !== 'string' ||
    typeof record.isMaterialChange !== 'boolean' ||
    typeof record.accepted !== 'boolean'
  ) {
    return null;
  }
  return body as TermsState;
}

/** `AuthorSession`: only the three members this feature relies on are required to be present. */
function narrowAuthorSession(body: unknown): AuthorSession | null {
  const record = asRecord(body);
  if (record === null) {
    return null;
  }
  if (
    typeof record.authorStatus !== 'string' ||
    typeof record.canUseEditor !== 'boolean' ||
    typeof record.cabinetReachable !== 'boolean'
  ) {
    return null;
  }
  return body as AuthorSession;
}

export interface CallOptions {
  readonly signal?: AbortSignal;
}

function callFor(
  method: 'GET' | 'POST',
  path: string,
  options: CallOptions,
): { readonly method: 'GET' | 'POST'; readonly path: string; readonly signal?: AbortSignal } {
  // `exactOptionalPropertyTypes` is on, so an explicit `signal: undefined` is a type error rather
  // than an omission. Spread it in only when there is one.
  return options.signal === undefined ? { method, path } : { method, path, signal: options.signal };
}

export async function getAuthorSession(
  transport: ApiTransport,
  options: CallOptions = {},
): Promise<AuthorSession> {
  const response = await transport(callFor('GET', API_PATHS.authorSession, options));
  if (response.status !== 200) {
    throw refuse(response, 'getAuthorSession');
  }
  const session = narrowAuthorSession(response.body);
  if (session === null) {
    throw shapeError(response, 'getAuthorSession', 'an AuthorSession');
  }
  return session;
}

export async function getPlatformContacts(
  transport: ApiTransport,
  options: CallOptions = {},
): Promise<PlatformContacts> {
  const response = await transport(callFor('GET', API_PATHS.platformContacts, options));
  if (response.status !== 200) {
    throw refuse(response, 'getPlatformContacts');
  }
  const contacts = narrowPlatformContacts(response.body);
  if (contacts === null) {
    throw shapeError(response, 'getPlatformContacts', 'a PlatformContacts');
  }
  return contacts;
}

export async function getCurrentTerms(
  transport: ApiTransport,
  options: CallOptions = {},
): Promise<TermsState> {
  const response = await transport(callFor('GET', API_PATHS.currentTerms, options));
  if (response.status !== 200) {
    throw refuse(response, 'getCurrentTerms');
  }
  const terms = narrowTermsState(response.body);
  if (terms === null) {
    throw shapeError(response, 'getCurrentTerms', 'a TermsState');
  }
  return terms;
}

/**
 * Record an acceptance. THE BODY IS `{ versionLabel }` AND NOTHING ELSE — the request schema is
 * `additionalProperties: false`, so an extra member is a 400, and the acceptance CHANNEL
 * (`'application'` vs `'cabinet_reaccept'`) is the server's to decide from which caller it is.
 *
 * There is no `Location` header on the 201 by design; the identifier is in the body, which is the
 * updated `TermsState`.
 */
export async function acceptTerms(
  transport: ApiTransport,
  request: TermsAcceptanceRequest,
  options: CallOptions = {},
): Promise<TermsState> {
  const base = callFor('POST', API_PATHS.termsAcceptances, options);
  const response = await transport({ ...base, body: { versionLabel: request.versionLabel } });
  if (response.status !== 201) {
    throw refuse(response, 'acceptTerms');
  }
  const terms = narrowTermsState(response.body);
  if (terms === null) {
    throw shapeError(response, 'acceptTerms', 'a TermsState');
  }
  return terms;
}

/** A version member of an agreement: the three strings, and the material flag where named. */
function isVersion(value: unknown, at: 'effectiveFrom' | 'acceptedAt'): boolean {
  const record = asRecord(value);
  return (
    record !== null &&
    typeof record.versionLabel === 'string' &&
    typeof record[at] === 'string' &&
    typeof record.contentUrl === 'string' &&
    (at === 'acceptedAt' || typeof record.isMaterialChange === 'boolean')
  );
}

/** `AgreementList`: every item with its code, title, what it blocks, its two flags and versions. */
function narrowAgreementList(body: unknown): AgreementList | null {
  const record = asRecord(body);
  if (record === null || !Array.isArray(record.items)) {
    return null;
  }
  const valid = record.items.every((item: unknown) => {
    const agreement = asRecord(item);
    return (
      agreement !== null &&
      typeof agreement.documentCode === 'string' &&
      typeof agreement.title === 'string' &&
      (agreement.blocks === 'submission' || agreement.blocks === 'payout') &&
      typeof agreement.owed === 'boolean' &&
      typeof agreement.newerVersionWaiting === 'boolean' &&
      (agreement.live === null || isVersion(agreement.live, 'effectiveFrom')) &&
      (agreement.accepted === null || isVersion(agreement.accepted, 'acceptedAt'))
    );
  });
  return valid ? (body as AgreementList) : null;
}

/**
 * `listAgreements` (design 25): the documents, what this author accepted, and what is owed - the
 * interstitial's list and Settings' "Your agreements".
 */
export async function listAgreements(
  transport: ApiTransport,
  options: CallOptions = {},
): Promise<AgreementList> {
  const response = await transport(callFor('GET', API_PATHS.agreements, options));
  if (response.status !== 200) {
    throw refuse(response, 'listAgreements');
  }
  const list = narrowAgreementList(response.body);
  if (list === null) {
    throw shapeError(response, 'listAgreements', 'an AgreementList');
  }
  return list;
}

/**
 * `acceptAgreements` (design 25): every document ticked, in ONE call - 1 to 4, distinct. An item
 * already accepted is a no-op rather than a 409; a 404 is an unknown or superseded version. It
 * answers the list, as `listAgreements` does. As with `acceptTerms`, the channel is the server's.
 */
export async function acceptAgreements(
  transport: ApiTransport,
  request: AgreementAcceptRequest,
  options: CallOptions = {},
): Promise<AgreementList> {
  const base = callFor('POST', API_PATHS.agreementAcceptances, options);
  const accept = request.accept.map(({ documentCode, versionLabel }) => ({
    documentCode,
    versionLabel,
  }));
  const response = await transport({ ...base, body: { accept } });
  // 201 is what the contract answers; a 200 is read the same way.
  if (response.status !== 200 && response.status !== 201) {
    throw refuse(response, 'acceptAgreements');
  }
  const list = narrowAgreementList(response.body);
  if (list === null) {
    throw shapeError(response, 'acceptAgreements', 'an AgreementList');
  }
  return list;
}
